/**
 * klubb-plp-strip — scroll controls for the club-page strips (focus, packages,
 * upsell; D58).
 *
 * Each [data-klubb-strip] contains:
 *   [data-klubb-strip-rail]  the scroll-snap row (scrollbar hidden by .klubb-rail)
 *   [data-klubb-strip-prev]  prev arrow, [data-klubb-strip-next] next arrow
 *
 * The module only reports state to CSS (is-scrollable / is-at-end on the strip):
 * the arrows show when the strip overflows, the touch fade on the right edge while
 * there is more to the right. Arrows page the rail by one viewport width and wrap
 * around at the ends (never disabled, as in klubb-cart-xsell.js).
 *
 * klubb-plp-filter.js#swapPage replaces the children of [data-klubb-plp-main]
 * (strips included) without an event, so a MutationObserver wires the new strips.
 */

// Distance in px that still counts as "at the edge" (sub-pixel rounding).
const EDGE = 1;

/** Which controls are relevant for a rail at this scroll position. */
export function railState(scrollLeft, clientWidth, scrollWidth) {
  const max = scrollWidth - clientWidth;
  return {
    scrollable: max > EDGE,
    atEnd: scrollLeft >= max - EDGE,
  };
}

/**
 * Where an arrow click scrolls to: one rail width in `dir` (1 = next, -1 = prev),
 * clamped to the ends, wrapping from the end to the start and the other way round.
 * Keep in step with `page` in klubb-cart-xsell.js.
 */
export function pageTarget(dir, scrollLeft, clientWidth, scrollWidth) {
  const max = scrollWidth - clientWidth;
  if (dir > 0 && scrollLeft >= max - EDGE) return 0;
  if (dir < 0 && scrollLeft <= EDGE) return max;
  return Math.max(0, Math.min(max, scrollLeft + dir * clientWidth));
}

export function wireStrip(strip) {
  if (!strip || strip.dataset.klubbStripWired === 'true') return;
  const rail = strip.querySelector('[data-klubb-strip-rail]');
  if (!rail) return;
  strip.dataset.klubbStripWired = 'true';

  function update() {
    const s = railState(rail.scrollLeft, rail.clientWidth, rail.scrollWidth);
    strip.classList.toggle('is-scrollable', s.scrollable);
    strip.classList.toggle('is-at-end', s.atEnd);
  }

  function page(dir) {
    const left = pageTarget(dir, rail.scrollLeft, rail.clientWidth, rail.scrollWidth);
    // Explicit behavior overrides CSS scroll-behavior, so reduced-motion
    // must be honored here in JS, not in the stylesheet.
    const reduced = typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    rail.scrollTo({ left, behavior: reduced ? 'auto' : 'smooth' });
  }

  const prev = strip.querySelector('[data-klubb-strip-prev]');
  const next = strip.querySelector('[data-klubb-strip-next]');
  if (prev) prev.addEventListener('click', () => page(-1));
  if (next) next.addEventListener('click', () => page(1));

  rail.addEventListener('scroll', update, { passive: true });
  if (typeof ResizeObserver !== 'undefined') {
    new ResizeObserver(update).observe(rail);
  } else {
    window.addEventListener('resize', update);
  }
  update();
}

function wireAll(root) {
  root.querySelectorAll('[data-klubb-strip]').forEach(wireStrip);
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  const init = () => {
    wireAll(document);
    const main = document.querySelector('[data-klubb-plp-main]');
    if (main && typeof MutationObserver !== 'undefined') {
      new MutationObserver(() => wireAll(main)).observe(main, { childList: true });
    }
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}
